import { View, Text, Pressable, ScrollView, StyleSheet, TextInput, Switch, Platform, Share, Linking, KeyboardAvoidingView } from 'react-native'
import { useState, useEffect, useRef, useCallback } from 'react'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useRouter, useLocalSearchParams } from 'expo-router'
import { User, Mail, Bell, Shield, LogOut, ChevronRight, ChevronDown, Save, Download, Trash2, Check, X, Link as LinkIcon, Smartphone, Fingerprint, Info, MessageSquareHeart } from 'lucide-react-native'
import { BottomSheetModal, BottomSheetBackdrop, BottomSheetView } from '@gorhom/bottom-sheet'
import { useAuth } from '../../src/auth/AuthProvider'
import { sentryCaptureException } from '../../src/lib/sentry'
import { apiClient } from '../../src/api/client'
import { storage, USER_ID_KEY } from '../../src/utils/storage'
import { colors, spacing, borderRadius, typography, shadows } from '../../src/theme'
import { haptics } from '../../src/utils/haptics'
import { SkeletonCard, SkeletonBlock } from '../../src/components/Skeleton'
import PressableScale from '../../src/components/PressableScale'
import * as Clipboard from 'expo-clipboard'
import { registerForPushNotifications, clearPushToken } from '../../src/notifications'
import { isBiometricsAvailable, isBiometricsEnabled, setBiometricsEnabled, getBiometricType, getBiometricLabel } from '../../src/auth/biometrics'
import ContentContainer from '../../src/components/ContentContainer'
import { events } from '../../src/lib/posthog'
import EmailScanResults from '../../src/components/EmailScanResults'
import EmailProviderModal from '../../src/components/EmailProviderModal'
import { toast } from '../../src/components/Toast'
import { feedbackTrigger } from '../../src/components/FeedbackButton'
import { useFeedbackTrigger } from '../../src/hooks/useFeedbackTrigger'
import { useSafeAPI } from '../../src/hooks/useSafeAPI'
import { useResponsive } from '../../src/hooks/useResponsive'

export { ScreenErrorBoundary as ErrorBoundary } from '../../src/components/ScreenErrorBoundary'

type Section = 'profile' | 'email' | 'notifications' | 'privacy' | 'gdpr'

export default function SettingsScreen() {
  const { user, logout, refreshUser } = useAuth()
  const { isDesktop } = useResponsive()
  const router = useRouter()
  const params = useLocalSearchParams<{ section?: string; email_connected?: string; account_id?: string; error?: string }>()
  const initialSection = params.section
  const [activeSection, setActiveSection] = useState<Section>(
    initialSection === 'email' || initialSection === 'notifications' || initialSection === 'privacy' || initialSection === 'gdpr'
      ? initialSection
      : 'profile'
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [userData, setUserData] = useState<any>(null)
  const [profileForm, setProfileForm] = useState({ full_name: '', email: '', phone: '' })
  const [preferences, setPreferences] = useState<any>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteText, setDeleteText] = useState('')
  const deleteSheetRef = useRef<BottomSheetModal>(null)
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; destructive?: boolean; icon?: React.ReactNode; action: () => void } | null>(null)
  const confirmSheetRef = useRef<BottomSheetModal>(null)

  useEffect(() => {
    if (showDeleteModal) { deleteSheetRef.current?.present(); haptics.heavy() }
    else { deleteSheetRef.current?.dismiss(); setDeleteText('') }
  }, [showDeleteModal])

  useEffect(() => {
    if (confirmAction) { confirmSheetRef.current?.present(); haptics.light() }
    else { confirmSheetRef.current?.dismiss() }
  }, [confirmAction])

  const renderDeleteBackdrop = useCallback(
    (props: any) => <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.5} pressBehavior="close" />,
    []
  )
  const renderConfirmBackdrop = useCallback(
    (props: any) => <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.5} pressBehavior="close" />,
    []
  )
  const [emailConnections, setEmailConnections] = useState<any[]>([])
  const [scanningConnectionId, setScanningConnectionId] = useState<string | null>(null)
  const [pbsForm, setPbsForm] = useState({ reg: '', account: '' })
  const [savingPbs, setSavingPbs] = useState(false)
  const [pushEnabled, setPushEnabled] = useState(false)
  const [pushRegistering, setPushRegistering] = useState(false)
  const [biometricsAvailable, setBiometricsAvailable] = useState(false)
  const [biometricsOn, setBiometricsOn] = useState(false)
  const [biometricLabel, setBiometricLabel] = useState('Biometrisk login')
  const [loadError, setLoadError] = useState(false)
  const [emailConsent, setEmailConsent] = useState(false)
  const [showProviderModal, setShowProviderModal] = useState(false)
  const emailCallbackProcessedRef = useRef(false)
  const [settingsExplored, setSettingsExplored] = useState(false)
  const visitedSectionsRef = useRef(new Set<string>([initialSection || 'profile']))
  const { safe, hadError, reset: resetSafeError } = useSafeAPI('settings')
  useFeedbackTrigger('settings_visited', 10_000, 'Er der noget du savner?', settingsExplored)

  useEffect(() => {
    events.screenViewed('settings')
    const load = async () => {
      try {
        const userId = user?.id || ''
        if (!userId) return
        resetSafeError()
        const [userRes, prefsRes, emailRes] = await Promise.all([
          safe(apiClient.users.getMe(userId), { data: null } as any),
          safe(apiClient.inbox.getPreferences(), { data: null } as any),
          safe(apiClient.email.status(), { data: { connections: [] } } as any),
        ])
        if (hadError.current) setLoadError(true)
        const data = userRes.data
        setUserData(data)
        setEmailConnections(emailRes.data?.connections || [])
        if (data?.payment_reg_masked) setPbsForm({ reg: data.payment_reg_masked, account: data.payment_account_masked || '' })
        // Check if push token is registered
        const storedPushToken = await storage.get('push_token')
        if (storedPushToken) setPushEnabled(true)
        // Check biometric availability
        const bioAvail = await isBiometricsAvailable()
        setBiometricsAvailable(bioAvail)
        if (bioAvail) {
          const bioOn = await isBiometricsEnabled()
          setBiometricsOn(bioOn)
          const bioType = await getBiometricType()
          setBiometricLabel(getBiometricLabel(bioType))
        }
        setProfileForm({
          full_name: data?.full_name || user?.full_name || '',
          email: data?.email || user?.email || '',
          phone: data?.phone || '',
        })
        setPreferences(prefsRes.data)
      } catch {
        // API interceptor handles error reporting
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [user?.id])

  // Handle OAuth redirect — sync + poll connections when user returns from email OAuth
  useEffect(() => {
    if (params.email_connected === 'true' && !emailCallbackProcessedRef.current) {
      emailCallbackProcessedRef.current = true
      setActiveSection('email')
      // Sync with Unipile + poll until connection appears (webhook may be delayed)
      let attempts = 0
      const poll = async () => {
        attempts++
        try {
          // Use sync endpoint with the specific account_id from OAuth redirect
          const res = await apiClient.email.sync(params.account_id)
          const connections = res.data?.connections || []
          setEmailConnections(connections)
          if (connections.length > 0) {
            toast.success('Email forbundet!')
            const newest = connections.reduce((a: any, b: any) =>
              new Date(b.connected_at) > new Date(a.connected_at) ? b : a
            )
            if (newest?.id && !newest.scan_consent_given_at) {
              apiClient.email.scanConsent(newest.id).catch(() => {})
            }
            // Clear URL params so re-navigation doesn't re-trigger poll
            router.setParams({})
            return // Done
          }
        } catch {}
        // Retry up to 5 times with 2s delay
        if (attempts < 5) {
          setTimeout(poll, 2000)
        } else {
          toast.error('Kunne ikke bekræfte email-forbindelsen. Prøv at genindlæse siden.')
          router.setParams({})
        }
      }
      poll()
    } else if (params.email_connected === 'false' && !emailCallbackProcessedRef.current) {
      emailCallbackProcessedRef.current = true
      setActiveSection('email')
      toast.error('Email tilslutning fejlede')
      router.setParams({})
    }
  }, [params.email_connected])

  // Feedback trigger: fire when user has visited 3+ unique sections (matches Next.js)
  useEffect(() => {
    visitedSectionsRef.current.add(activeSection)
    if (visitedSectionsRef.current.size >= 3) {
      setSettingsExplored(true)
    }
  }, [activeSection])

  const saveProfile = async () => {
    if (!profileForm.full_name?.trim()) {
      toast.error('Navn er påkrævet')
      return
    }
    if (profileForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileForm.email)) {
      toast.error('Ugyldig email-adresse')
      return
    }
    if (profileForm.phone && !/^[\d\s\+\-()]+$/.test(profileForm.phone)) {
      toast.error('Ugyldigt telefonnummer')
      return
    }
    setSaving(true)
    try {
      const userId = user?.id || ''
      // Strip country code prefix before saving — store only 8-digit local number
      const cleanPhone = profileForm.phone
        ? profileForm.phone.replace(/^(?:\+?45|0045)\s*/, '').replace(/[^0-9]/g, '').slice(0, 8)
        : ''
      await apiClient.users.update(userId, { ...profileForm, phone: cleanPhone })
      setUserData({ ...userData, ...profileForm })
      await refreshUser()
      events.settingsUpdated('profile')
      toast.success('Profil opdateret!')
    } catch (err: any) {
      sentryCaptureException(err, { action: 'save_profile' })
      toast.error('Kunne ikke gemme profil — vi er notificeret. Prøv igen.')
    } finally {
      setSaving(false)
    }
  }

  const updatePreference = async (category: string, key: string, value: boolean) => {
    if (!preferences) return
    try {
      const keyMap: Record<string, string> = {
        'provider_allow_contact': 'allow_provider_contact',
        'provider_share_data': 'share_data_with_providers',
      }
      const rawKey = `${category}_${key}`
      const updateKey = keyMap[rawKey] || rawKey
      await apiClient.inbox.updatePreferences({ [updateKey]: value })
      setPreferences({
        ...preferences,
        [category]: { ...(preferences[category] || {}), [key]: value },
      })
      toast.success('Indstillinger gemt')
    } catch {
      toast.error('Kunne ikke gemme indstillinger')
    }
  }

  const handleExportData = async () => {
    try {
      toast.success('Eksporterer dine data...')
      const userId = user?.id || ''
      const res = await apiClient.users.exportData(userId)
      const jsonString = typeof res.data === 'string' ? res.data : JSON.stringify(res.data, null, 2)

      if (Platform.OS === 'web') {
        // Web: create blob and trigger download
        const blob = new Blob([jsonString], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `forbrugeragent-data-${userId}.json`
        a.click()
        URL.revokeObjectURL(url)
        toast.success('Data eksporteret!')
      } else {
        // Native: use Share API to let user save/send the JSON
        await Share.share({
          title: 'ForbrugerAgenten — Mine Data',
          message: jsonString,
        })
        toast.success('Data eksporteret!')
      }
    } catch (err: any) {
      sentryCaptureException(err, { action: 'export_data' })
      toast.error('Kunne ikke eksportere data — vi er notificeret. Prøv igen.')
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteText !== 'SLET') return
    try {
      const userId = user?.id || ''
      await apiClient.users.delete(userId)
      toast.success('Din konto er slettet')
      logout()
    } catch (err: any) {
      sentryCaptureException(err, { action: 'delete_account' })
      toast.error('Kunne ikke slette konto — vi er notificeret. Prøv igen.')
    }
  }

  const savePbs = async () => {
    if (!pbsForm.reg || !pbsForm.account) {
      toast.error('Udfyld begge felter')
      return
    }
    setSavingPbs(true)
    try {
      const userId = user?.id || ''
      await apiClient.users.update(userId, { payment_reg: pbsForm.reg, payment_account: pbsForm.account })
      setUserData({ ...userData, has_payment_info: true, payment_reg: pbsForm.reg, payment_account: pbsForm.account })
      setPbsForm({ reg: '', account: '' })
      events.settingsUpdated('payment')
      toast.success('Betalingsoplysninger gemt!')
    } catch {
      toast.error('Kunne ikke gemme betalingsoplysninger')
    } finally {
      setSavingPbs(false)
    }
  }

  const handleEmailConnected = async () => {
    const emailRes = await apiClient.email.status().catch(() => ({ data: { connections: [] } }))
    const connections = emailRes.data?.connections || []
    setEmailConnections(connections)

    // Trigger GDPR-consented scan for the newest connection (user already checked consent checkbox)
    if (emailConsent && connections.length > 0) {
      const newest = connections.reduce((a: any, b: any) =>
        new Date(b.connected_at) > new Date(a.connected_at) ? b : a
      )
      if (newest?.id && !newest.scan_consent_given_at) {
        apiClient.email.scanConsent(newest.id).catch(() => {})
      }
    }
  }

  const disconnectEmail = async (connectionId: string) => {
    // Optimistic UI — remove from list immediately, rollback on failure
    const previous = emailConnections
    setEmailConnections(prev => prev.filter(c => c.id !== connectionId))
    toast.success('Email forbindelse fjernet')
    try {
      await apiClient.email.disconnect(connectionId)
    } catch {
      setEmailConnections(previous)
      toast.error('Kunne ikke fjerne forbindelse — prøv igen')
    }
  }

  const handleLogout = () => {
    setConfirmAction({
      title: 'Log ud',
      message: 'Er du sikker på at du vil logge ud?',
      destructive: true,
      icon: <LogOut size={22} color={colors.error} />,
      action: () => { events.logout(); logout() },
    })
  }

  /* Agent email copy removed — users don't need to see/use the agent email */

  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={[]}>
        <View style={styles.header}><Text style={styles.title}>Indstillinger</Text></View>
        <View style={{ padding: spacing.md, gap: spacing.md }}>
          {/* Tab strip skeleton */}
          <View style={{ flexDirection: 'row', gap: spacing.xs }}>
            {[80, 60, 50, 70, 50].map((w, i) => (
              <SkeletonBlock key={i} width={w} height={36} radius={borderRadius.sm} />
            ))}
          </View>
          {/* Profile card skeleton */}
          <View style={{ backgroundColor: colors.background, borderRadius: borderRadius.md, padding: spacing.md, gap: spacing.sm }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <SkeletonBlock width={56} height={56} radius={28} />
              <View style={{ flex: 1, gap: 6 }}>
                <SkeletonBlock width="60%" height={18} />
                <SkeletonBlock width="80%" height={14} />
              </View>
            </View>
          </View>
          {/* Settings rows skeleton */}
          {[0, 1, 2].map(i => (
            <View key={i} style={{ backgroundColor: colors.background, borderRadius: borderRadius.md, padding: spacing.md, gap: spacing.sm }}>
              <SkeletonBlock width={100} height={14} />
              <SkeletonBlock width="90%" height={40} radius={borderRadius.sm} />
              <SkeletonBlock width="90%" height={40} radius={borderRadius.sm} />
            </View>
          ))}
        </View>
      </SafeAreaView>
    )
  }

  return (
    <SafeAreaView style={styles.container} edges={[]}>
      <ContentContainer>
      <View style={styles.header}>
        <Text style={styles.title}>Indstillinger</Text>
        <Text style={styles.subtitle}>Administrer din konto og præferencer</Text>
      </View>

      {/* Error banner */}
      {loadError && (
        <View style={styles.errorBanner}>
          <Info size={16} color="#92400e" />
          <Text style={styles.errorBannerText}>Noget gik galt — vores team er notificeret.</Text>
          <Pressable onPress={() => {
            const userId = user?.id || ''
            if (userId) {
              setLoadError(false)
              setLoading(true)
              Promise.all([
                apiClient.users.getMe(userId).catch(() => { setLoadError(true); return { data: null } }),
                apiClient.inbox.getPreferences().catch(() => { setLoadError(true); return { data: null } }),
                apiClient.email.status().catch(() => { setLoadError(true); return { data: { connections: [] } } }),
              ]).then(([userRes, prefsRes, emailRes]) => {
                if (userRes.data) {
                  setUserData(userRes.data)
                  setProfileForm({
                    full_name: userRes.data?.full_name || '',
                    email: userRes.data?.email || '',
                    phone: userRes.data?.phone || '',
                  })
                }
                setPreferences(prefsRes.data)
                setEmailConnections(emailRes.data?.connections || [])
              }).finally(() => setLoading(false))
            }
          }}>
            <Text style={styles.errorBannerRetry}>Prøv igen</Text>
          </Pressable>
        </View>
      )}

      {/* Section tabs */}
      <View style={styles.tabs}>
        <View style={styles.tabsContent}>
          {([
            { id: 'profile' as const, label: 'Profil' },
            { id: 'email' as const, label: 'Email' },
            { id: 'notifications' as const, label: 'Notiser' },
            { id: 'privacy' as const, label: 'Privatliv' },
            { id: 'gdpr' as const, label: 'GDPR' },
          ] as const).map(({ id, label }) => (
            <PressableScale
              key={id}
              accessibilityRole="tab"
              accessibilityState={{ selected: activeSection === id }}
              accessibilityLabel={label}
              style={[styles.tab, activeSection === id && styles.tabActive]}
              onPress={() => { haptics.selection(); setActiveSection(id) }}
              haptic="light"
            >
              <Text style={[styles.tabText, activeSection === id && styles.tabTextActive]}>{label}</Text>
            </PressableScale>
          ))}
        </View>
      </View>

      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined} keyboardVerticalOffset={Platform.OS === 'ios' ? 100 : 0}>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        {/* Profile Section */}
        {activeSection === 'profile' && (
          <View style={styles.section}>
            {/* Profile card */}
            <View style={styles.profileCard}>
              <View style={styles.avatarCircle}>
                <User size={24} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.profileName}>{user?.full_name || 'Bruger'}</Text>
                <Text style={styles.profileEmail}>{user?.email || ''}</Text>
              </View>
            </View>

            <View style={styles.formSection}>
              <Text style={styles.formSectionTitle}>Profil Oplysninger</Text>
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Navn</Text>
                <TextInput
                  style={styles.formInput}
                  value={profileForm.full_name}
                  onChangeText={(t) => setProfileForm(f => ({ ...f, full_name: t }))}
                  placeholderTextColor={colors.text.muted}
                />
              </View>
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Email</Text>
                <TextInput
                  style={styles.formInput}
                  value={profileForm.email}
                  onChangeText={(t) => setProfileForm(f => ({ ...f, email: t }))}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  placeholderTextColor={colors.text.muted}
                />
              </View>
              <View style={styles.formGroup}>
                <Text style={styles.formLabel}>Telefon</Text>
                <TextInput
                  style={styles.formInput}
                  value={profileForm.phone}
                  onChangeText={(t) => setProfileForm(f => ({ ...f, phone: t }))}
                  keyboardType="phone-pad"
                  placeholderTextColor={colors.text.muted}
                />
              </View>

              {/* Agent Email removed — users don't need to see/use the agent email */}

              <Pressable accessibilityRole="button" accessibilityLabel={saving ? 'Gemmer profil' : 'Gem ændringer'} style={styles.saveButton} onPress={saveProfile} disabled={saving}>
                <Save size={16} color={colors.text.inverse} />
                <Text style={styles.saveButtonText}>{saving ? 'Gemmer...' : 'Gem Ændringer'}</Text>
              </Pressable>
            </View>

            {/* PBS / Payment info */}
            <View style={styles.pbsSection}>
              <Text style={styles.sectionTitle}>Betalingsoplysninger (PBS)</Text>
              <Text style={styles.sectionDesc}>
                {userData?.has_payment_info
                  ? 'Dine betalingsoplysninger er gemt og bruges automatisk ved fremtidige aftaler.'
                  : 'Tilføj reg/konto så vi automatisk kan videregive det til nye udbydere.'}
              </Text>
              {userData?.has_payment_info ? (
                <View style={styles.pbsActiveCard}>
                  <Check size={18} color={colors.success} />
                  <Text style={styles.pbsActiveText}>Betalingsoplysninger er gemt</Text>
                  <Pressable
                    onPress={() => {
                      setPbsForm({ reg: userData?.payment_reg || '', account: userData?.payment_account || '' })
                      setUserData({ ...userData, has_payment_info: false })
                    }}
                  >
                    <Text style={{ ...typography.small, color: colors.text.muted, textDecorationLine: 'underline' }}>Opdater</Text>
                  </Pressable>
                </View>
              ) : (
                <View style={styles.pbsForm}>
                  <View style={styles.pbsRow}>
                    <View style={[styles.formGroup, { flex: 1 }]}>
                      <Text style={styles.formLabel}>Reg.nr.</Text>
                      <TextInput
                        style={styles.formInput}
                        value={pbsForm.reg}
                        onChangeText={(t) => setPbsForm(f => ({ ...f, reg: t.replace(/\D/g, '') }))}
                        placeholder="1234"
                        keyboardType="number-pad"
                        maxLength={4}
                        placeholderTextColor={colors.text.muted}
                      />
                    </View>
                    <View style={[styles.formGroup, { flex: 2 }]}>
                      <Text style={styles.formLabel}>Kontonummer</Text>
                      <TextInput
                        style={styles.formInput}
                        value={pbsForm.account}
                        onChangeText={(t) => setPbsForm(f => ({ ...f, account: t.replace(/\D/g, '') }))}
                        placeholder="1234567890"
                        keyboardType="number-pad"
                        maxLength={10}
                        placeholderTextColor={colors.text.muted}
                      />
                    </View>
                  </View>
                  <Text style={{ ...typography.small, color: colors.text.muted, marginTop: 2 }}>
                    Gemmes krypteret. Bruges til at opsætte Betalingsservice hos nye udbydere.
                  </Text>
                  <Pressable style={styles.saveButton} onPress={savePbs} disabled={savingPbs}>
                    <Save size={16} color={colors.text.inverse} />
                    <Text style={styles.saveButtonText}>{savingPbs ? 'Gemmer...' : 'Gem betalingsoplysninger'}</Text>
                  </Pressable>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Email Section */}
        {activeSection === 'email' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Email Forbindelser</Text>
            <Text style={styles.sectionDesc}>
              Forbind din email så vi kan handle på dine vegne:
            </Text>
            <View style={{ gap: 6, marginTop: 4 }}>
              {[
                'Send klager og opsigelser direkte fra din mailbox',
                'Modtag og organiser svar fra selskaber automatisk',
                'Find eksisterende aftaler via automatisk scanning',
              ].map((item, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 6 }}>
                  <Check size={14} color={colors.success} style={{ marginTop: 1 }} />
                  <Text style={{ ...typography.small, color: colors.text.secondary, flex: 1 }}>{item}</Text>
                </View>
              ))}
            </View>

            {emailConnections.length > 0 ? (
              emailConnections.map((conn: any) => {
                const isExpired = conn.last_error === 'grant_expired'
                const hasError = !!conn.last_error && !isExpired
                const isActive = conn.is_active !== false && !isExpired && !hasError
                const statusColor = isExpired ? colors.warning : hasError ? colors.error : isActive ? colors.success : colors.text.muted
                const statusBg = isExpired ? colors.warningMuted : hasError ? colors.errorMuted : isActive ? colors.successLight : colors.borderLight
                const statusLabel = isExpired ? 'Skal genforbindes' : hasError ? 'Fejl' : isActive ? 'Forbundet' : 'Inaktiv'
                const providerLabel = { gmail: 'Gmail', outlook: 'Outlook', onecom: 'one.com', yousee: 'YouSee', telia: 'Telia', telenor: 'Telenor', stofa: 'Stofa', waoo: 'Waoo', jubii: 'Jubii', yahoo: 'Yahoo', icloud: 'iCloud', fullrate: 'Fullrate', tre: '3' }[conn.provider as string] || conn.provider
                const formatDate = (d: string | null) => d ? new Date(d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', year: 'numeric' }) : null

                return (
                  <View key={conn.id} style={styles.emailConnCard}>
                    <View style={styles.emailConnHeader}>
                      <View style={[styles.emailIconBox, { backgroundColor: statusBg }]}>
                        <Mail size={18} color={statusColor} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <Text style={styles.emailConnEmail} numberOfLines={1}>{conn.email_address || conn.email || conn.provider}</Text>
                          {conn.is_primary && (
                            <View style={{ backgroundColor: colors.primaryLight, paddingHorizontal: 6, paddingVertical: 1, borderRadius: 8 }}>
                              <Text style={{ ...typography.small, color: colors.primaryDark, fontWeight: '600' }}>Primær</Text>
                            </View>
                          )}
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <View style={[styles.emailStatusDot, { backgroundColor: statusColor }]} />
                          <Text style={[styles.emailConnStatus, { color: statusColor }]}>{statusLabel}</Text>
                          <Text style={{ ...typography.small, color: colors.text.muted }}>{providerLabel}</Text>
                        </View>
                        {conn.connected_at && (
                          <Text style={{ ...typography.small, color: colors.text.muted, marginTop: 2 }}>
                            Forbundet: {formatDate(conn.connected_at)}
                            {conn.last_sync_at ? ` — Synk: ${formatDate(conn.last_sync_at)}` : ''}
                          </Text>
                        )}
                        {isExpired && (
                          <Text style={{ ...typography.small, color: '#d97706', marginTop: 2 }}>
                            Din forbindelse er udløbet. Forbind igen for at fortsætte.
                          </Text>
                        )}
                      </View>
                    </View>
                    <View style={styles.emailConnActions}>
                      <Pressable
                        style={styles.emailScanBtn}
                        onPress={() => {
                          const connId = conn.id
                          setConfirmAction({
                            title: 'Scan din indbakke',
                            message: 'GDPR-samtykke: Ved at starte scanningen giver du ForbrugerAgenten tilladelse til at gennemsøge din indbakke for emails fra el-, mobil- og bredbåndsselskaber. Vi gemmer kun afsender-domæner og antal emails — vi læser ikke indholdet af dine emails.',
                            icon: <Shield size={22} color={colors.primary} />,
                            action: async () => {
                              setScanningConnectionId(connId)
                              try {
                                await apiClient.email.scanConsent(connId)
                                const scanPromise = apiClient.email.fullScan(connId)
                                const timeoutPromise = new Promise((_, reject) =>
                                  setTimeout(() => reject(new Error('timeout')), 120_000)
                                )
                                const res = await Promise.race([scanPromise, timeoutPromise]) as any
                                const providerCount = res.data?.providers_found || res.data?.providers?.length || 0
                                toast.success(`Scan fuldført! ${providerCount} selskab${providerCount !== 1 ? 'er' : ''} fundet.`)
                              } catch (err: any) {
                                if (err?.message === 'timeout') {
                                  toast.error('Scanningen tog for lang tid. Prøv igen senere.')
                                } else {
                                  const detail = err?.response?.data?.detail || 'Scanning fejlede'
                                  toast.error(detail)
                                }
                              } finally {
                                setScanningConnectionId(null)
                              }
                            },
                          })
                        }}
                      >
                        <Text style={styles.emailScanBtnText}>Scan min indbakke</Text>
                      </Pressable>
                      <Pressable
                        accessibilityRole="button"
                        accessibilityLabel="Fjern email-forbindelse"
                        style={styles.emailDisconnectBtn}
                        onPress={() => {
                          const connId = conn.id
                          setConfirmAction({
                            title: 'Fjern forbindelse?',
                            message: `Vil du fjerne ${conn.email_address || conn.email || conn.provider}?`,
                            destructive: true,
                            icon: <LinkIcon size={22} color={colors.error} />,
                            action: () => disconnectEmail(connId),
                          })
                        }}
                      >
                        <X size={14} color={colors.error} />
                        <Text style={styles.emailDisconnectText}>Fjern</Text>
                      </Pressable>
                    </View>
                  </View>
                )
              })
            ) : (
              <View style={styles.emailEmptyCard}>
                <Mail size={24} color={colors.text.muted} />
                <Text style={styles.emailEmptyText}>Ingen emails tilsluttet endnu</Text>
              </View>
            )}

            {/* Scan Results */}
            {scanningConnectionId && (
              <View style={{ marginTop: spacing.sm }}>
                <EmailScanResults
                  connectionId={scanningConnectionId}
                  onComplete={() => setScanningConnectionId(null)}
                />
              </View>
            )}

            {/* GDPR consent checkbox */}
            <Pressable
              accessibilityRole="checkbox"
              accessibilityState={{ checked: emailConsent }}
              accessibilityLabel="Jeg giver samtykke til email-forbindelse"
              style={styles.consentRow}
              onPress={() => setEmailConsent(!emailConsent)}
            >
              <View style={[styles.consentCheckbox, emailConsent && styles.consentCheckboxChecked]}>
                {emailConsent && <Check size={14} color={colors.text.inverse} />}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.consentLabel}>Jeg giver samtykke</Text>
                <Text style={styles.consentDesc}>
                  ForbrugerAgenten må sende og læse emails på mine vegne til el-, mobil- og bredbåndsselskaber, og scanne min indbakke for eksisterende aftaler. Vi læser aldrig private beskeder. Jeg kan til enhver tid frakoble min email.
                </Text>
              </View>
            </Pressable>

            {/* Connect email button → opens provider modal */}
            <Pressable
              style={[styles.saveButton, !emailConsent && { opacity: 0.4 }]}
              onPress={() => emailConsent ? setShowProviderModal(true) : toast.error('Du skal acceptere samtykke først')}
              disabled={!emailConsent}
            >
              <Mail size={16} color={colors.text.inverse} />
              <Text style={styles.saveButtonText}>Forbind email</Text>
            </Pressable>

            <EmailProviderModal
              visible={showProviderModal}
              onClose={() => setShowProviderModal(false)}
              onConnected={handleEmailConnected}
              redirectTo="settings"
              consentGiven={emailConsent}
            />

            {/*
              Email forwarding toggles (forward_bills/notices/marketing/
              provider_other/fuldmagt) were removed 2026-04-15. The toggles
              wrote to Pydantic ghost fields that had no DB columns — data
              was silently dropped. The product policy is now: provider
              replies go to app-inbox + Expo push, never to user.email.
              See docs/EMAIL_NOTIFICATIONS.md for the full rule.
            */}

          </View>
        )}

        {/* Notifications Section */}
        {activeSection === 'notifications' && !preferences && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Notifikationsindstillinger</Text>
            <View style={{ alignItems: 'center', paddingVertical: spacing.xl, gap: spacing.md }}>
              <Bell size={32} color={colors.text.muted} />
              <Text style={{ color: colors.text.muted, fontSize: 15, textAlign: 'center' }}>
                Kunne ikke indlæse notifikationsindstillinger
              </Text>
              <PressableScale
                style={{ backgroundColor: colors.primary, paddingVertical: spacing.sm, paddingHorizontal: spacing.lg, borderRadius: borderRadius.md }}
                onPress={() => {
                  const reload = async () => {
                    try {
                      const prefsRes = await apiClient.inbox.getPreferences()
                      setPreferences(prefsRes.data)
                    } catch {
                      toast.error('Kunne ikke indlæse indstillinger — prøv igen')
                    }
                  }
                  reload()
                }}
              >
                <Text style={{ color: '#fff', fontWeight: '600', fontSize: 15 }}>Prøv igen</Text>
              </PressableScale>
            </View>
          </View>
        )}
        {activeSection === 'notifications' && preferences && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Notifikationsindstillinger</Text>

            {/* Push Notification Toggle (native only) */}
            {Platform.OS !== 'web' && (
              <View style={styles.pushCard}>
                <View style={styles.pushCardHeader}>
                  <Smartphone size={18} color={colors.primary} />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.pushCardTitle}>Push Notifikationer</Text>
                    <Text style={styles.pushCardDesc}>
                      Få besked når du modtager nye tilbud eller svar fra selskaber
                    </Text>
                  </View>
                  <Switch
                    accessibilityRole="switch"
                    accessibilityLabel="Push notifikationer"
                    accessibilityState={{ checked: pushEnabled, disabled: pushRegistering }}
                    value={pushEnabled}
                    disabled={pushRegistering}
                    onValueChange={async (val) => {
                      haptics.selection()
                      if (val) {
                        setPushRegistering(true)
                        try {
                          const userId = user?.id || ''
                          const token = await registerForPushNotifications(userId)
                          setPushEnabled(!!token)
                          if (!token) {
                            toast.info('Giv tilladelse til push notifikationer i dine enhedsindstillinger.')
                          }
                        } catch {
                          toast.error('Kunne ikke aktivere push notifikationer')
                        } finally {
                          setPushRegistering(false)
                        }
                      } else {
                        // Disable: clear token from both local storage and backend
                        await clearPushToken(user?.id)
                        setPushEnabled(false)
                      }
                    }}
                    trackColor={{ false: Platform.select({ ios: "#E5E5EA", default: colors.border }), true: colors.primary }}
                    thumbColor={Platform.select({ ios: undefined, default: '#ffffff' })}
                    ios_backgroundColor={Platform.select({ ios: "#E5E5EA", default: colors.border })}
                  />
                </View>
              </View>
            )}

            {/*
              Email Notifikationer: the only NotificationPreferences email
              field the backend actually reads is `email_quotes` (via the
              inbox filter). `email_system`, `email_reminders`,
              `email_newsletter`, `inbox_system`, `allow_provider_contact`,
              and `share_data_with_providers` are defined on the model but
              never consulted by senders — removed from UI to avoid
              dead-setting confusion. Push toggles are kept because
              push_notification_service.py:49-60 reads them.
              See docs/EMAIL_NOTIFICATIONS.md for the canonical email spec.
            */}
            {[
              {
                id: 'email', title: 'Email Notifikationer', icon: Mail, iconColor: colors.primary,
                items: [
                  { key: 'quotes', label: 'Tilbud fra selskaber', desc: 'Modtag email når du får nye tilbud' },
                  { key: 'marketing', label: 'Reklamer fra selskaber', desc: 'Tillad selskaber at sende dig reklamer' },
                ],
              },
              {
                id: 'inbox', title: 'Indbakke Visning', icon: Bell, iconColor: '#9333ea',
                items: [
                  { key: 'marketing', label: 'Vis reklamer', desc: 'Vis reklamer fra selskaber i din indbakke' },
                ],
              },
            ].map((group, idx) => (
              <NotifGroup
                key={group.id}
                group={group}
                preferences={preferences}
                onUpdate={updatePreference}
                defaultOpen={idx === 0}
              />
            ))}
          </View>
        )}

        {/* Privacy Section */}
        {activeSection === 'privacy' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Privatliv & Sikkerhed</Text>

            {/* Biometric Toggle (native only) */}
            {Platform.OS !== 'web' && biometricsAvailable && (
              <View style={styles.biometricCard}>
                <View style={styles.biometricHeader}>
                  <Fingerprint size={20} color={colors.primary} />
                  <View style={{ flex: 1 }}>
                    <Text style={styles.biometricTitle}>{biometricLabel}</Text>
                    <Text style={styles.biometricDesc}>
                      Lås appen op med {biometricLabel.toLowerCase()} ved åbning
                    </Text>
                  </View>
                  <Switch
                    accessibilityRole="switch"
                    accessibilityLabel={biometricLabel}
                    accessibilityState={{ checked: biometricsOn }}
                    value={biometricsOn}
                    onValueChange={async (val) => {
                      haptics.selection()
                      await setBiometricsEnabled(val)
                      setBiometricsOn(val)
                      toast.success(val
                        ? `${biometricLabel} aktiveret`
                        : `${biometricLabel} deaktiveret`)
                    }}
                    trackColor={{ false: Platform.select({ ios: "#E5E5EA", default: colors.border }), true: colors.primary }}
                    thumbColor={Platform.select({ ios: undefined, default: '#ffffff' })}
                    ios_backgroundColor={Platform.select({ ios: "#E5E5EA", default: colors.border })}
                  />
                </View>
              </View>
            )}

            <View style={styles.privacyCard}>
              <Shield size={20} color={colors.success} />
              <View style={{ flex: 1 }}>
                <Text style={styles.privacyTitle}>Din data er sikker</Text>
                <Text style={styles.privacyText}>
                  Vi bruger MitID til sikker identifikation og krypterer alle dine data. Vi deler aldrig dine oplysninger uden dit samtykke.
                </Text>
              </View>
            </View>
            <View style={styles.privacyList}>
              <Text style={styles.privacyListTitle}>Hvad vi indsamler:</Text>
              {['Kontaktoplysninger (navn, email, telefon)', 'Forbrugsdata fra dine regninger', 'Information om dine nuværende aftaler', 'Kommunikation med selskaber'].map((item, i) => (
                <View key={i} style={styles.privacyListItem}>
                  <Check size={14} color={colors.success} />
                  <Text style={styles.privacyListText}>{item}</Text>
                </View>
              ))}
            </View>
            <View style={styles.privacyList}>
              <Text style={styles.privacyListTitle}>Hvad vi ALDRIG gør:</Text>
              {['Sælger dine data til tredjeparter', 'Spammer dig med uønskede emails', 'Deler dine oplysninger uden samtykke', 'Skifter dine aftaler uden din godkendelse'].map((item, i) => (
                <View key={i} style={styles.privacyListItem}>
                  <X size={14} color={colors.error} />
                  <Text style={styles.privacyListText}>{item}</Text>
                </View>
              ))}
            </View>
            <Pressable
              style={styles.privacyLink}
              onPress={() => router.push('/privatlivspolitik' as any)}
            >
              <Text style={styles.privacyLinkText}>Se fuld privatlivspolitik</Text>
            </Pressable>
          </View>
        )}

        {/* GDPR Section */}
        {activeSection === 'gdpr' && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Dine Data (GDPR)</Text>
            <Text style={styles.gdprIntro}>
              I henhold til GDPR har du fulde rettigheder over dine data.{'\n'}
              Du kan når som helst eksportere eller slette alle dine oplysninger.
            </Text>

            <Pressable accessibilityRole="button" accessibilityLabel="Eksporter dine data" style={styles.gdprExportCard} onPress={handleExportData}>
              <Download size={20} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={styles.gdprCardTitle}>Eksporter Dine Data</Text>
                <Text style={styles.gdprCardText}>Download alle dine data i et læsbart format (JSON). Dette inkluderer profil, aftaler, tilbud, og kommunikation.</Text>
              </View>
              <ChevronRight size={16} color={colors.text.muted} />
            </Pressable>

            <Pressable accessibilityRole="button" accessibilityLabel="Slet din konto" style={styles.gdprDeleteCard} onPress={() => { setDeleteText(''); setShowDeleteModal(true) }}>
              <Trash2 size={20} color={colors.error} />
              <View style={{ flex: 1 }}>
                <Text style={styles.gdprDeleteTitle}>Slet Din Konto</Text>
                <Text style={styles.gdprDeleteText}>Dette vil permanent slette din konto og alle tilknyttede data. Denne handling kan ikke fortrydes.</Text>
              </View>
              <ChevronRight size={16} color={colors.text.muted} />
            </Pressable>

            <View style={styles.retentionCard}>
              <Text style={styles.retentionTitle}>Dataopbevaringspolitik</Text>
              <Text style={styles.retentionText}>
                Vi opbevarer dine data så længe din konto er aktiv. Inaktive konti slettes automatisk efter 24 måneder uden aktivitet.
              </Text>
            </View>
          </View>
        )}

        {/* Feedback + Logout — only on Profil tab */}
        {activeSection === 'profile' && (
          <>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Giv feedback"
              style={styles.feedbackButton}
              onPress={() => feedbackTrigger.open('manual', 'Hvad kan vi gøre bedre?')}
            >
              <MessageSquareHeart size={20} color={colors.primary} />
              <Text style={styles.feedbackText}>Giv feedback</Text>
              <ChevronRight size={16} color={colors.text.muted} style={{ marginLeft: 'auto' }} />
            </Pressable>

            <Pressable accessibilityRole="button" accessibilityLabel="Log ud" style={styles.logoutButton} onPress={handleLogout}>
              <LogOut size={20} color={colors.error} />
              <Text style={styles.logoutText}>Log ud</Text>
            </Pressable>

            <Text style={styles.version}>ForbrugerAgenten v1.0.0</Text>
          </>
        )}
      </ScrollView>
      </KeyboardAvoidingView>

      {/* Confirmation Bottom Sheet */}
      <BottomSheetModal
        ref={confirmSheetRef}
        snapPoints={['38%']}
        onDismiss={() => setConfirmAction(null)}
        backdropComponent={renderConfirmBackdrop}
        handleIndicatorStyle={{ backgroundColor: '#CBD5E1', width: 36, height: 4, borderRadius: 2 }}
        backgroundStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24 }}
        containerStyle={isDesktop ? { left: 288 } as any : undefined}
        enablePanDownToClose
      >
        <BottomSheetView style={styles.modalContent}>
          {confirmAction?.icon && (
            <View style={[styles.modalIconCircle, { backgroundColor: confirmAction?.destructive ? colors.errorLight : colors.primaryLight }]}>
              {confirmAction.icon}
            </View>
          )}
          <Text style={styles.modalTitle}>{confirmAction?.title}</Text>
          <Text style={styles.modalText}>{confirmAction?.message}</Text>
          <View style={styles.modalDivider} />
          <View style={styles.modalButtons}>
            <Pressable style={styles.modalCancelBtn} onPress={() => setConfirmAction(null)}>
              <Text style={styles.modalCancelText}>Annuller</Text>
            </Pressable>
            <Pressable
              style={[confirmAction?.destructive ? styles.modalDeleteBtn : styles.confirmActionBtn]}
              onPress={async () => { await confirmAction?.action(); setConfirmAction(null) }}
            >
              <Text style={[confirmAction?.destructive ? styles.modalDeleteText : styles.confirmActionText]}>
                {confirmAction?.destructive ? confirmAction?.title : 'Bekræft'}
              </Text>
            </Pressable>
          </View>
        </BottomSheetView>
      </BottomSheetModal>

      {/* Delete Account Bottom Sheet */}
      <BottomSheetModal
        ref={deleteSheetRef}
        snapPoints={['50%']}
        onDismiss={() => setShowDeleteModal(false)}
        backdropComponent={renderDeleteBackdrop}
        handleIndicatorStyle={{ backgroundColor: '#CBD5E1', width: 36, height: 4, borderRadius: 2 }}
        backgroundStyle={{ borderTopLeftRadius: 24, borderTopRightRadius: 24 }}
        containerStyle={isDesktop ? { left: 288 } as any : undefined}
        enablePanDownToClose
        keyboardBehavior="interactive"
        keyboardBlurBehavior="restore"
      >
        <BottomSheetView style={styles.modalContent}>
          <View style={styles.modalDeleteIcon}>
            <Trash2 size={26} color={colors.error} />
          </View>
          <Text style={styles.modalTitle}>Slet din konto permanent?</Text>
          <Text style={styles.modalText}>Alle dine data, jagter, tilbud og beskeder vil blive permanent slettet.</Text>
          <Text style={styles.modalWarning}>Denne handling kan ikke fortrydes.</Text>

          <Text style={styles.modalConfirmLabel}>
            Skriv <Text style={styles.modalConfirmCode}>SLET</Text> for at bekræfte
          </Text>
          <TextInput
            style={styles.modalConfirmInput}
            value={deleteText}
            onChangeText={setDeleteText}
            placeholder="Skriv SLET her"
            placeholderTextColor={colors.text.muted}
            autoCapitalize="characters"
          />

          <View style={styles.modalButtons}>
            <Pressable accessibilityRole="button" accessibilityLabel="Annuller sletning" style={styles.modalCancelBtn} onPress={() => setShowDeleteModal(false)}>
              <Text style={styles.modalCancelText}>Annuller</Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Slet min konto permanent"
              accessibilityState={{ disabled: deleteText !== 'SLET' }}
              style={[styles.modalDeleteBtn, deleteText !== 'SLET' && { opacity: 0.4 }]}
              onPress={() => { haptics.error(); handleDeleteAccount() }}
              disabled={deleteText !== 'SLET'}
            >
              <Text style={styles.modalDeleteText}>Slet min konto</Text>
            </Pressable>
          </View>
        </BottomSheetView>
      </BottomSheetModal>
      </ContentContainer>
    </SafeAreaView>
  )
}

function NotifGroup({ group, preferences, onUpdate, defaultOpen = false }: { group: any; preferences: any; onUpdate: (cat: string, key: string, val: boolean) => void; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const Icon = group.icon
  const prefs = preferences?.[group.id] || {}
  const activeCount = group.items.filter((item: any) => prefs[item.key]).length
  const totalCount = group.items.length

  return (
    <View style={styles.notifGroup}>
      <Pressable accessibilityRole="button" accessibilityLabel={`${group.title}, ${activeCount} af ${totalCount} aktive`} accessibilityState={{ expanded: isOpen }} style={styles.notifGroupHeader} onPress={() => setIsOpen(!isOpen)}>
        <Icon size={18} color={group.iconColor} />
        <View style={{ flex: 1 }}>
          <Text style={styles.notifGroupTitle}>{group.title}</Text>
          <Text style={styles.notifGroupCount}>{activeCount} af {totalCount} aktive</Text>
        </View>
        <ChevronDown size={16} color={colors.text.muted} style={isOpen ? { transform: [{ rotate: '180deg' }] } : undefined} />
      </Pressable>
      {isOpen && (
        <View style={styles.notifItems}>
          {group.items.map((item: any) => (
            <View key={item.key} style={styles.notifItem}>
              <View style={{ flex: 1 }}>
                <Text style={styles.notifItemLabel}>{item.label}</Text>
                {item.desc && <Text style={styles.notifItemDesc}>{item.desc}</Text>}
              </View>
              <Switch
                accessibilityRole="switch"
                accessibilityLabel={item.label}
                accessibilityState={{ checked: prefs[item.key] ?? false }}
                value={prefs[item.key] ?? false}
                onValueChange={(val) => { haptics.selection(); onUpdate(group.id, item.key, val) }}
                trackColor={{ false: Platform.select({ ios: "#E5E5EA", default: colors.border }), true: colors.primary }}
                thumbColor={Platform.select({ ios: undefined, default: '#ffffff' })}
                ios_backgroundColor={Platform.select({ ios: "#E5E5EA", default: colors.border })}
              />
            </View>
          ))}
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.surface },
  header: { padding: spacing.md, backgroundColor: colors.background, borderBottomWidth: 1, borderBottomColor: colors.border },
  title: { ...typography.h2, color: colors.text.primary },
  subtitle: { ...typography.small, color: colors.text.secondary, marginTop: 2 },
  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginHorizontal: spacing.md, marginTop: spacing.xs,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    backgroundColor: '#fffbeb', borderWidth: 1, borderColor: '#fde68a',
    borderRadius: borderRadius.md,
  },
  errorBannerText: { ...typography.small, color: '#92400e', flex: 1 },
  errorBannerRetry: { ...typography.small, color: '#78350f', fontWeight: '600', textDecorationLine: 'underline' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  tabs: { backgroundColor: colors.background, borderBottomWidth: 1, borderBottomColor: colors.border },
  tabsContent: {
    flexDirection: 'row', gap: 6,
    paddingHorizontal: spacing.md, marginTop: spacing.sm, marginBottom: spacing.sm,
  },
  tab: {
    flex: 1, paddingHorizontal: 6, paddingVertical: 8, borderRadius: borderRadius.sm,
    backgroundColor: colors.background, borderWidth: 1, borderColor: colors.border,
    minHeight: 44, justifyContent: 'center' as const, alignItems: 'center' as const,
  },
  tabActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  tabText: { ...typography.small, color: colors.text.secondary, fontWeight: '500' },
  tabTextActive: { color: colors.text.inverse },
  scroll: { flex: 1 },
  scrollContent: { padding: spacing.md, gap: spacing.md, paddingBottom: 100 },
  section: { gap: spacing.sm },
  sectionTitle: { ...typography.bodyBold, color: colors.text.primary },
  sectionDesc: { ...typography.small, color: colors.text.secondary, lineHeight: 18 },
  profileCard: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.md, backgroundColor: colors.background,
    borderRadius: borderRadius.lg, padding: spacing.lg, ...shadows.sm, borderWidth: 1, borderColor: colors.borderLight,
  },
  avatarCircle: {
    width: 52, height: 52, borderRadius: 26, backgroundColor: colors.primaryLight,
    alignItems: 'center', justifyContent: 'center',
  },
  profileName: { ...typography.h3, color: colors.text.primary },
  profileEmail: { ...typography.caption, color: colors.text.secondary, marginTop: 2 },
  formSection: {
    backgroundColor: colors.background, borderRadius: borderRadius.lg,
    padding: spacing.md, gap: spacing.sm, ...shadows.sm, borderWidth: 1, borderColor: colors.borderLight,
  },
  formSectionTitle: { ...typography.bodyBold, color: colors.text.primary, fontSize: 15 },
  formGroup: { gap: 4 },
  formLabel: { ...typography.small, color: colors.text.secondary, fontWeight: '500' },
  formInput: {
    borderWidth: 1, borderColor: colors.border, borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm, paddingVertical: 10, ...typography.body, color: colors.text.primary,
  },
  formHint: { ...typography.small, color: colors.text.muted },
  agentEmailRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  agentEmailText: {
    flex: 1, paddingHorizontal: spacing.sm, paddingVertical: 10,
    backgroundColor: colors.surface, borderRadius: borderRadius.sm, borderWidth: 1, borderColor: colors.border,
    ...typography.caption, color: colors.text.secondary,
  },
  copyButton: {
    flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.md,
    paddingVertical: 10, backgroundColor: colors.primary, borderRadius: borderRadius.sm,
    minHeight: 44,
  },
  copyText: { ...typography.small, color: colors.text.inverse, fontWeight: '500' },
  saveButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs,
    paddingVertical: 14, backgroundColor: colors.primary, borderRadius: borderRadius.md, marginTop: spacing.sm,
    ...shadows.colored(colors.primary),
  },
  saveButtonText: { ...typography.captionBold, color: colors.text.inverse, fontSize: 15 },
  notifGroup: {
    backgroundColor: colors.background, borderRadius: borderRadius.md,
    overflow: 'hidden', ...shadows.sm,
  },
  notifGroupHeader: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md,
  },
  notifGroupTitle: { ...typography.captionBold, color: colors.text.primary },
  notifGroupCount: { ...typography.small, color: colors.text.muted, marginTop: 1 },
  notifItems: { borderTopWidth: 1, borderTopColor: colors.borderLight },
  notifItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderBottomWidth: 1, borderBottomColor: colors.borderLight,
  },
  notifItemLabel: { ...typography.caption, color: colors.text.primary },
  notifItemDesc: { ...typography.small, color: colors.text.muted, marginTop: 1 },
  privacyCard: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm,
    padding: spacing.md, backgroundColor: colors.successLight, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.successMuted,
  },
  privacyTitle: { ...typography.captionBold, color: colors.successDark },
  privacyText: { ...typography.small, color: colors.success, lineHeight: 18, marginTop: 2 },
  privacyList: { gap: spacing.xs },
  privacyListTitle: { ...typography.captionBold, color: colors.text.primary },
  privacyListItem: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  privacyListText: { ...typography.caption, color: colors.text.secondary },
  privacyLink: {
    paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    backgroundColor: colors.primaryLight, borderRadius: borderRadius.sm,
    alignSelf: 'flex-start',
  },
  privacyLinkText: { ...typography.captionBold, color: colors.primary },
  gdprIntro: { ...typography.caption, color: colors.text.secondary },
  gdprExportCard: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    padding: spacing.md, backgroundColor: colors.primaryLight, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: '#bfdbfe',
  },
  gdprCardTitle: { ...typography.captionBold, color: colors.primaryDark },
  gdprCardText: { ...typography.small, color: colors.primary },
  gdprDeleteCard: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    padding: spacing.md, backgroundColor: '#fef2f2', borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: '#fecaca',
  },
  gdprDeleteTitle: { ...typography.captionBold, color: '#991b1b' },
  gdprDeleteText: { ...typography.small, color: '#dc2626' },
  retentionCard: {
    padding: spacing.md, backgroundColor: colors.surface, borderRadius: borderRadius.md,
  },
  retentionTitle: { ...typography.captionBold, color: colors.text.primary },
  retentionText: { ...typography.small, color: colors.text.secondary, marginTop: 2 },
  feedbackButton: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    padding: spacing.md, backgroundColor: colors.background, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.border, minHeight: 52,
  },
  feedbackText: { ...typography.body, color: colors.text.primary },
  logoutButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.sm,
    padding: spacing.md, backgroundColor: colors.errorLight, borderRadius: borderRadius.md, minHeight: 52,
  },
  logoutText: { ...typography.bodyBold, color: colors.error },
  version: { ...typography.small, color: colors.text.muted, textAlign: 'center' },
  emailConnCard: {
    backgroundColor: colors.background, borderRadius: borderRadius.md, padding: spacing.md,
    gap: spacing.sm, ...shadows.sm,
  },
  emailConnHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  emailIconBox: {
    width: 40, height: 40, borderRadius: borderRadius.sm,
    alignItems: 'center', justifyContent: 'center',
  },
  emailConnEmail: { ...typography.captionBold, color: colors.text.primary },
  emailConnStatus: { ...typography.small, color: colors.text.secondary },
  emailStatusDot: { width: 8, height: 8, borderRadius: 4 },
  emailConnActions: { flexDirection: 'row', gap: spacing.sm },
  emailScanBtn: {
    flex: 1, paddingVertical: 8, backgroundColor: colors.primaryLight, borderRadius: borderRadius.sm,
    alignItems: 'center', borderWidth: 1, borderColor: '#bfdbfe',
  },
  emailScanBtnText: { ...typography.small, color: colors.primary, fontWeight: '500' },
  emailDisconnectBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: spacing.sm,
    paddingVertical: 8, backgroundColor: '#fef2f2', borderRadius: borderRadius.sm,
    borderWidth: 1, borderColor: '#fecaca',
  },
  emailDisconnectText: { ...typography.small, color: colors.error, fontWeight: '500' },
  emailEmptyCard: {
    alignItems: 'center', gap: spacing.xs, padding: spacing.lg,
    backgroundColor: colors.background, borderRadius: borderRadius.md,
    ...shadows.sm,
  },
  emailEmptyText: { ...typography.caption, color: colors.text.muted },
  emailConnectBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs,
    paddingVertical: spacing.sm, backgroundColor: colors.primary, borderRadius: borderRadius.sm,
  },
  emailConnectBtnText: { ...typography.captionBold, color: colors.text.inverse },
  consentRow: {
    flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm,
    padding: spacing.sm, backgroundColor: colors.surface, borderRadius: borderRadius.md,
    borderWidth: 1, borderColor: colors.border,
  },
  consentCheckbox: {
    width: 22, height: 22, borderRadius: 4, borderWidth: 2, borderColor: colors.border,
    alignItems: 'center', justifyContent: 'center', marginTop: 2,
  },
  consentCheckboxChecked: {
    backgroundColor: colors.primary, borderColor: colors.primary,
  },
  consentLabel: { ...typography.captionBold, color: colors.text.primary },
  consentDesc: { ...typography.small, color: colors.text.muted, marginTop: 2, lineHeight: 16 },
  pushCard: {
    backgroundColor: colors.background, borderRadius: borderRadius.md,
    padding: spacing.md, ...shadows.sm,
  },
  pushCardHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  pushCardTitle: { ...typography.captionBold, color: colors.text.primary },
  pushCardDesc: { ...typography.small, color: colors.text.secondary, marginTop: 2 },
  biometricCard: {
    backgroundColor: colors.background, borderRadius: borderRadius.md,
    padding: spacing.md, ...shadows.sm,
  },
  biometricHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  biometricTitle: { ...typography.captionBold, color: colors.text.primary },
  biometricDesc: { ...typography.small, color: colors.text.secondary, marginTop: 2 },
  forwardingCard: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    backgroundColor: colors.background, borderRadius: borderRadius.md, padding: spacing.md,
    ...shadows.sm,
  },
  forwardingTitle: { ...typography.captionBold, color: colors.text.primary },
  forwardingDesc: { ...typography.small, color: colors.text.secondary, marginTop: 2 },
  pbsSection: { marginTop: spacing.md, gap: spacing.sm },
  pbsActiveCard: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md,
    backgroundColor: colors.successLight, borderRadius: borderRadius.md, borderWidth: 1, borderColor: colors.successMuted,
  },
  pbsActiveText: { ...typography.captionBold, color: colors.successDark, flex: 1 },
  pbsForm: { gap: spacing.sm },
  pbsRow: { flexDirection: 'row', gap: spacing.sm },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: spacing.md },
  modalContent: {
    backgroundColor: colors.background, padding: spacing.lg, paddingTop: spacing.sm, width: '100%',
  },
  modalIconCircle: {
    width: 52, height: 52, borderRadius: 26,
    alignItems: 'center', justifyContent: 'center', alignSelf: 'center', marginBottom: spacing.md,
  },
  modalDeleteIcon: {
    width: 56, height: 56, borderRadius: 28, backgroundColor: colors.errorLight,
    alignItems: 'center', justifyContent: 'center', alignSelf: 'center', marginBottom: spacing.md,
  },
  modalTitle: { ...typography.h3, color: colors.text.primary, textAlign: 'center' },
  modalText: { ...typography.caption, color: colors.text.secondary, textAlign: 'center', marginTop: spacing.sm, lineHeight: 20 },
  modalWarning: {
    ...typography.captionBold, color: colors.error, textAlign: 'center', marginTop: spacing.sm,
    backgroundColor: colors.errorLight, paddingVertical: spacing.xs, paddingHorizontal: spacing.sm,
    borderRadius: borderRadius.sm, overflow: 'hidden',
  },
  modalDivider: { height: 1, backgroundColor: colors.border, marginVertical: spacing.lg },
  modalConfirmLabel: { ...typography.caption, color: colors.text.secondary, marginTop: spacing.md },
  modalConfirmCode: { fontWeight: '700', backgroundColor: colors.errorLight, color: colors.error },
  modalConfirmInput: {
    borderWidth: 1.5, borderColor: colors.border, borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md, paddingVertical: 14, marginTop: spacing.sm,
    ...typography.body, color: colors.text.primary, textAlign: 'center', letterSpacing: 4,
  },
  modalButtons: { flexDirection: 'row', gap: spacing.sm },
  modalCancelBtn: {
    flex: 1, paddingVertical: 14, backgroundColor: 'transparent',
    borderWidth: 1, borderColor: colors.border, borderRadius: borderRadius.md, alignItems: 'center',
  },
  modalCancelText: { ...typography.captionBold, color: colors.text.secondary },
  modalDeleteBtn: {
    flex: 1, paddingVertical: 14, backgroundColor: colors.error, borderRadius: borderRadius.md, alignItems: 'center',
  },
  modalDeleteText: { ...typography.captionBold, color: colors.text.inverse },
  confirmActionBtn: {
    flex: 1, paddingVertical: 14, backgroundColor: colors.primary, borderRadius: borderRadius.md, alignItems: 'center',
  },
  confirmActionText: { ...typography.captionBold, color: colors.text.inverse },
})
